import { AlertCircle, CheckCircle, Info, X } from "lucide-react";

import type React from "react";
import { useEffect, useState } from "react";


type AlertType = "success" | "error" | "info" | "warning";

interface AlertProps {
  type: AlertType;
  message: string;
  onClose?: () => void;
  autoClose?: boolean;
  autoCloseDuration?: number;
}

const Alert: React.FC<AlertProps> = ({
  type,
  message,
  onClose,
  autoClose = true,
  autoCloseDuration = 5000,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onClose?.();
      }, autoCloseDuration);

      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDuration, onClose]);

  if (!isVisible) return null;

  const iconMap = {
    success: <CheckCircle className="text-green-500" size={20} />,
    error: <AlertCircle className="text-red-500" size={20} />,
    info: <Info className="text-blue-500" size={20} />,
    warning: <AlertCircle className="text-yellow-500" size={20} />,
  };

  const bgColorMap = {
    success: "bg-green-50 border-green-100",
    error: "bg-red-50 border-red-100",
    info: "bg-blue-50 border-blue-100",
    warning: "bg-yellow-50 border-yellow-100",
  };

  const textColorMap = {
    success: "text-green-700",
    error: "text-red-700",
    info: "text-blue-700",
    warning: "text-yellow-700",
  };

  return (
    <div
      className={`fixed top-4 right-4 z-50 p-4 rounded-lg border ${bgColorMap[type]} shadow-lg max-w-md transition-all duration-300`}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0 mr-3">{iconMap[type]}</div>
        <div className="flex-1">
          <p className={`text-sm font-medium ${textColorMap[type]}`}>{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            onClose?.();
          }}
          className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-500"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default Alert;
